import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Panel from "../../components/ui/Panel/Panel.jsx";
import Button from "../../components/ui/Button/Button.jsx";
import Select from "../../components/forms/Select/Select.jsx";
import { useApp } from "../../hooks/useApp.js";
import { money } from "../../utils/format.js";
import { getSubscriptionPackages, getSubscriptionHistory } from "../../services/subscriptionService.js";
import SubscriptionPurchaseModal from "./SubscriptionPurchaseModal.jsx";
import PaymentReceiptModal from "./PaymentReceiptModal.jsx";

// Statuses a package purchase can be receipted for - PENDING/FAILED never
// actually charged anything, so there's nothing for a receipt to confirm.
const RECEIPTABLE_PAYMENT_STATUSES = new Set(["SUCCESS", "REFUNDED"]);

// Shapes a purchase-history row (UserSubscriptionResponse) into the
// {id, source, confirmationNumber, description, date, amount, status,
// paymentMethod, businessName} PaymentReceiptModal already knows how to
// render - same field names a Dashboard > Payments row (ServiceFeeHistoryResponse)
// carries, so the modal needs no changes. source=SUBSCRIPTION_PURCHASE tells the
// backend `h.id` is a UserSubscription id (ServiceFeeServiceImpl.getReceipt).
function toReceiptPayment(h) {
  return {
    id: h.id,
    source: "SUBSCRIPTION_PURCHASE",
    confirmationNumber: h.confirmationNumber,
    description: `${h.categoryName} package — ${h.packageQuantity} unit${h.packageQuantity === 1 ? "" : "s"} (${h.totalReturnsPurchased} return credits)`,
    date: h.purchaseDate,
    amount: h.finalAmount,
    status: h.paymentStatus,
    paymentMethod: "Card",
    businessName: null,
  };
}

const STATUS_STYLES = {
  ACTIVE: { label: "Active", bg: "var(--accent-soft)", color: "var(--accent)" },
  EXHAUSTED: { label: "Exhausted", bg: "var(--panel-2)", color: "var(--muted)" },
  CANCELLED: { label: "Cancelled", bg: "var(--danger-soft)", color: "var(--danger)" },
  EXPIRED: { label: "Expired", bg: "var(--panel-2)", color: "var(--muted)" },
  PENDING: { label: "Pending", bg: "var(--amber-soft)", color: "var(--amber)" },
};

function StatusChip({ status }) {
  const s = STATUS_STYLES[status] || { label: status || "—", bg: "var(--panel-2)", color: "var(--muted)" };
  return (
    <span
      className="inline-flex items-center gap-1 px-[9px] py-1 rounded-[20px] text-[12px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// One card per category (SOLO, DUO-2, etc.), with a dropdown to pick which
// package tier (5/25/50/100/200 returns, per the pricing sheet) to buy - not
// a separate card per tier. Price/discount/returns-included update to match
// whichever tier is selected; nothing here is computed client-side, it's all
// read straight off the matching entry in category.packages.
function CategoryCard({ category, onBuy }) {
  const packages = category.packages || [];
  const [selectedId, setSelectedId] = useState(packages[0]?.packageId);
  const pkg = packages.find((p) => String(p.packageId) === String(selectedId)) || packages[0];

  if (!pkg) return null;
  const hasDiscount = Number(pkg.discountAmount) > 0;

  return (
    <Panel padded pop>
      <div className="font-disp uppercase tracking-[.1em] text-[11px] text-[var(--accent)] font-semibold">
        {category.name}
      </div>
      {category.description && <div className="text-[13px] text-[var(--muted)] mt-1">{category.description}</div>}

      <div className="mt-3.5 mb-1 text-[12px] font-semibold text-[var(--muted)] uppercase tracking-[0.04em]">
        Number of returns
      </div>
      <Select value={pkg.packageId} onChange={(e) => setSelectedId(e.target.value)}>
        {packages.map((p) => (
          <option key={p.packageId} value={p.packageId}>
            {p.packageQuantity} returns
          </option>
        ))}
      </Select>

      <div className="text-[13px] text-[var(--muted)] mt-3">
        File up to <strong className="text-[var(--ink)]">{pkg.returnsIncluded}</strong> return
        {pkg.returnsIncluded === 1 ? "" : "s"}
      </div>
      {/* Credits do not expire - there is no expiry column on the package
          payload and none enforced at redemption (owner-confirmed 2026-08-20).
          This previously read "Valid for 1 year from purchase", a restriction
          the product does not impose. Do not reinstate without a real expiry. */}
      <div className="text-[12px] text-[var(--muted)] mb-3">Credits never expire</div>

      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-[22px] font-extrabold text-[var(--ink)] tracking-[-0.02em]">{money(pkg.finalAmount)}</span>
        {hasDiscount && <span className="text-[13px] text-[var(--muted)] line-through">{money(pkg.baseAmount)}</span>}
      </div>
      {hasDiscount && (
        <div className="text-[12px] font-semibold text-[var(--ok)] mb-3">
          Save {money(pkg.discountAmount)} (
          {pkg.discountType === "PERCENTAGE" ? `${Number(pkg.discountValue)}% off` : "discount applied"})
        </div>
      )}

      <Button
        block
        onClick={() => onBuy({ ...pkg, categoryCode: category.code, categoryName: category.name })}
        className="mt-2"
      >
        Buy this package →
      </Button>
    </Panel>
  );
}

const Subscriptions = () => {
  const { showToast } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  // Set only when the filer got here via PaymentDetailsStep's "View plans"
  // upsell (mid-return, no subscription yet) - see that step's navigate call.
  // A purchase made from there should drop the filer straight back into their
  // in-progress return instead of leaving them stranded on this catalog page.
  const returnTo = location.state?.returnTo;
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null); // the package tile currently being purchased
  const [receipt, setReceipt] = useState(null); // { payment, autoSend }

  const loadAll = async () => {
    try {
      const [packages, purchases] = await Promise.all([getSubscriptionPackages(), getSubscriptionHistory()]);
      setCatalog(packages || []);
      setHistory(purchases || []);
    } catch (err) {
      showToast(err.message || "Couldn't load subscription packages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activePurchases = history.filter((h) => h.subscriptionStatus === "ACTIVE");
  const totalRemaining = activePurchases.reduce((sum, h) => sum + (h.returnsRemaining || 0), 0);

  const handlePurchaseSuccess = () => {
    setBuying(null);
    // Came from the mid-return upsell - go straight back to that step (the
    // subscription credit is now available there) instead of parking the
    // filer here on the catalog page.
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    loadAll();
  };

  return (
    <div className="max-w-[1180px] mx-auto px-[22px] py-[26px] pb-[70px]">
      <div className="mb-5">
        <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
          Subscriptions
        </div>
        <h1 className="text-[28px] leading-[1.1] mt-1">Return-credit packages</h1>
        <p className="text-[var(--muted)] max-w-[62ch]">
          Buy a discounted bundle of return credits and file returns without paying the per-vehicle service fee each
          time — or keep paying as you go, whichever works for you
        </p>
      </div>

      {activePurchases.length > 0 && (
        <Panel padded className="mb-6">
          <div className="text-[11px] font-bold text-[var(--muted)] uppercase tracking-[0.08em] mb-3">
            Your active packages
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            {activePurchases.map((h) => (
              <div key={h.id} className="border border-[var(--line)] rounded-[var(--r-sm)] p-3">
                <div className="text-[13px] font-bold text-[var(--ink)]">
                  {h.categoryName} — {h.packageQuantity === 1 ? "1 unit" : `${h.packageQuantity} units`}
                </div>
                <div className="text-[13px] text-[var(--muted)] mt-1">
                  {h.returnsRemaining} of {h.totalReturnsPurchased} returns remaining
                </div>
              </div>
            ))}
          </div>
          <div className="text-[13px] text-[var(--muted)] mt-3 pt-3 border-t border-[var(--line)]">
            <strong className="text-[var(--ink)]">{totalRemaining}</strong> total return credit
            {totalRemaining === 1 ? "" : "s"} available across all active packages.
          </div>
        </Panel>
      )}

      <div className="mb-2 text-[13px] font-bold text-[var(--ink)]">Available packages</div>
      {loading ? (
        <div className="p-8 text-center text-[var(--muted)]">Loading…</div>
      ) : catalog.length === 0 ? (
        <Panel padded className="mb-6">
          <div className="p-4 text-center text-[var(--muted)]">No subscription packages are available right now.</div>
        </Panel>
      ) : (
        <div className="grid gap-[14px] mb-6 sm:grid-cols-2 lg:grid-cols-3">
          {catalog.map((cat) => (
            <CategoryCard key={cat.categoryId} category={cat} onBuy={setBuying} />
          ))}
        </div>
      )}

      <Panel className="overflow-hidden">
        <div className="py-4 px-5 border-b border-[var(--line)] flex items-center justify-between">
          <div className="text-[13px] font-bold text-[var(--ink)]">Purchase history</div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[var(--muted)]">Loading…</div>
        ) : history.length === 0 ? (
          <div className="p-10 text-center text-[var(--muted)]">
            Your subscription purchases will appear here once you buy your first package.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[920px]">
              <thead>
                <tr className="border-b border-[var(--line)]">
                  {["Date", "Category", "Package", "Returns", "Used", "Remaining", "Amount", "Status", "Receipt"].map((h) => (
                    <th
                      key={h}
                      className="text-[12px] font-semibold text-[var(--muted)] uppercase tracking-[0.06em] py-3 px-4 text-left bg-[var(--panel-2)]"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-b border-[var(--line)] last:border-b-0 hover:bg-[var(--accent-soft)] transition-colors">
                    <td className="py-3.5 px-4 text-[13px] text-[var(--muted)] whitespace-nowrap">{fmtDate(h.purchaseDate)}</td>
                    <td className="py-3.5 px-4 text-[14px] font-semibold text-[var(--ink)]">{h.categoryName}</td>
                    <td className="py-3.5 px-4 text-[13px] text-[var(--muted)]">
                      {h.packageQuantity} unit{h.packageQuantity === 1 ? "" : "s"}
                    </td>
                    <td className="py-3.5 px-4 text-[13px] text-[var(--ink)]">{h.totalReturnsPurchased}</td>
                    <td className="py-3.5 px-4 text-[13px] text-[var(--ink)]">{h.returnsUsed}</td>
                    <td className="py-3.5 px-4 text-[13px] text-[var(--ink)] font-semibold">{h.returnsRemaining}</td>
                    <td className="py-3.5 px-4 text-[14px] font-bold text-[var(--ink)] whitespace-nowrap">{money(h.finalAmount)}</td>
                    <td className="py-3.5 px-4">
                      <StatusChip status={h.subscriptionStatus} />
                    </td>
                    <td className="py-3.5 px-4">
                      {RECEIPTABLE_PAYMENT_STATUSES.has(h.paymentStatus) ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="!text-xs !px-3 !py-1.5"
                          onClick={() => setReceipt({ payment: toReceiptPayment(h), autoSend: false })}
                        >
                          ↓ Receipt
                        </Button>
                      ) : (
                        <span className="text-[12px] text-[var(--muted)]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {buying && (
        <SubscriptionPurchaseModal pkg={buying} showToast={showToast} onClose={() => setBuying(null)} onSuccess={handlePurchaseSuccess} />
      )}

      {receipt && (
        <PaymentReceiptModal
          payment={receipt.payment}
          autoSend={receipt.autoSend}
          onClose={() => setReceipt(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default Subscriptions;
